import { matchPath } from 'react-router-dom';
import { sep } from 'path';

import { Organisation } from '@futrli-sdk/organisations';

import { PREDICT_PRODUCT_SLUG, QUERY_PARAMS, ROUTE_PARAMS, ROUTES } from '../../app-core/constants';
import { getParamFromRoute, interpolatePathParams, joinRouteMatchPath, joinRouteMatchUrl } from '../../path';
import { getWorkspaceDetailSettingsUrl, getWorkspaceDetailUrl } from '../../workspaces/utils/routes-utils';

export * from './routes/consolidations';

export const getOriginLocation = () => window.location.origin;

export const getCallbackUrl = (path: string) => new URL(`${path}${sep}`, getOriginLocation()).href;

/**
 * Gets the organisations URL of a workspace.
 * @param {string} workspaceId
 * @returns URL
 */
export const getOrganisationsUrl = (workspaceId: string): string =>
  joinRouteMatchUrl('', getWorkspaceDetailUrl(workspaceId), ROUTES.ORGANISATIONS_ROUTE);

/**
 * Gets the organisation detail URL
 * @param {string} workspaceId
 * @param {string} organisationId
 * @returns URL
 */
export const getOrganisationDetailUrl = (workspaceId: string, organisationId: string): string =>
  interpolatePathParams(joinRouteMatchUrl('', getOrganisationsUrl(workspaceId), ROUTES.ORGANISATION_BY_ID_ROUTE), {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
  });

/**
 * Gets the organisation detail URL
 * @param {string} workspaceId
 * @param {string} organisationId
 * @returns URL
 */
export const getOrganisationPredictUrl = (workspaceId: string, organisationId: string): string =>
  interpolatePathParams(
    joinRouteMatchUrl('', getOrganisationsUrl(workspaceId), ROUTES.ORGANISATION_BY_ID_ROUTE, '/', PREDICT_PRODUCT_SLUG),
    {
      [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
    }
  );

/**
 * Gets the organisations settings URL of a workspace.
 * @param {string} workspaceId
 * @return URL
 */
export const getOrganisationsSettingsUrl = (workspaceId: string): string =>
  joinRouteMatchUrl('', getWorkspaceDetailSettingsUrl(workspaceId), ROUTES.ORGANISATIONS_ROUTE);

/**
 * Gets the organisations settings URL of a workspace.
 * @param {string} workspaceId
 * @return URL
 */
export const getUsersSettingsUrl = (workspaceId: string): string =>
  joinRouteMatchUrl('', getWorkspaceDetailSettingsUrl(workspaceId), ROUTES.USERS_ROUTE);

/**
 * Gets the organisation detail settings URL of a workspace.
 * @param {string} workspaceId
 * @param {string} organisationId
 * @returns URL
 */
export const getOrganisationDetailSettingsUrl = (workspaceId: string, organisationId: string): string =>
  interpolatePathParams(
    joinRouteMatchUrl('', getOrganisationsSettingsUrl(workspaceId), ROUTES.ORGANISATION_BY_ID_ROUTE),
    {
      [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
    }
  );

/**
 * Gets the url for adding integrations to an Organisation
 * @param {string} workspaceId
 * @param {string} orgId
 * @returns {string}
 */
export const getAddIntegatrionsToOrgUrl = (workspaceId: string, orgId: string): string =>
  joinRouteMatchUrl(
    '',
    ROUTES.INTEGRATIONS_ROUTE,
    `?${QUERY_PARAMS.WORKSPACE_ID}=${workspaceId}&${QUERY_PARAMS.ORG_ID}=${orgId}`
  );

export const matchOrgsPath = (pathname: string, isExact?: boolean): boolean => {
  const orgsPath = joinRouteMatchPath(
    '',
    ROUTES.WORKSPACES_ROUTE,
    ROUTES.WORKSPACE_BY_ID_ROUTE,
    ROUTES.ORGANISATIONS_ROUTE
  );

  const pathPattern = isExact ? orgsPath : `${orgsPath}/*`;
  const matchedPath = matchPath(
    {
      path: pathPattern,
      end: isExact
    },
    pathname
  );

  return matchedPath !== null;
};

export const matchConsolidationsPath = (pathname: string, isExact?: boolean): boolean => {
  const consolidationsPath = joinRouteMatchPath(
    '',
    ROUTES.WORKSPACES_ROUTE,
    ROUTES.WORKSPACE_BY_ID_ROUTE,
    ROUTES.CONSOLIDATIONS_ROUTE
  );

  const pathPattern = isExact ? consolidationsPath : `${consolidationsPath}/*`;
  const matchedPath = matchPath(
    {
      path: pathPattern,
      end: isExact
    },
    pathname
  );

  return matchedPath !== null;
};

export const matchOrgDetailPath = (pathname: string, isExact?: boolean) => {
  const orgPath = joinRouteMatchPath(
    '',
    ROUTES.WORKSPACES_ROUTE,
    ROUTES.WORKSPACE_BY_ID_ROUTE,
    ROUTES.ORGANISATIONS_ROUTE,
    ROUTES.ORGANISATION_BY_ID_ROUTE
  );

  const pathPattern = isExact ? orgPath : `${orgPath}/*`;
  const matchedPath = matchPath(
    {
      path: pathPattern,
      end: isExact || false
    },
    pathname
  );

  // The workspace listing tabs use the same path segment as an organisation ID.
  // They are reserved route names, not organisation detail pages.
  if (
    matchedPath?.params[ROUTE_PARAMS.ORGANISATION_ID] === ROUTES.ORGANISATIONS_ROUTE.slice(1) ||
    matchedPath?.params[ROUTE_PARAMS.ORGANISATION_ID] === ROUTES.CONSOLIDATIONS_ROUTE.slice(1)
  ) {
    return null;
  }

  return matchedPath;
};

/**
 * Checks whether the location passed as parameter matches with the products path or not.
 * @param {string} pathname
 * @param {boolean} isExact
 * @param {boolean|undefined} checkProductModule - Adds the product module at the end of the path to check if it matches.
 * @returns {boolean}
 */
export const matchOrgProductPath = (pathname: string, isExact?: boolean, checkProductModule?: boolean): boolean => {
  const productOrgPath = joinRouteMatchPath(
    '',
    ROUTES.WORKSPACES_ROUTE,
    ROUTES.WORKSPACE_BY_ID_ROUTE,
    ROUTES.ORGANISATIONS_ROUTE,
    ROUTES.ORGANISATION_BY_ID_ROUTE,
    ROUTES.PRODUCT_SLUG_ROUTE,
    checkProductModule ? ROUTES.PRODUCT_MODULE : ''
  );

  const pathPattern = isExact ? productOrgPath : `${productOrgPath}/*`;
  const matchedPath = matchPath(
    {
      path: pathPattern,
      end: isExact || false
    },
    pathname
  );

  return matchedPath !== null;
};

/**
 * Checks whether the location passed as parameter matches with the products path or not into the organisation settings.
 * @param {string} pathname
 * @returns {PathMatch<string> | null}
 */
export const matchOrgSettingsProductPath = (pathname: string): ReturnType<typeof matchPath> => {
  const path = joinRouteMatchPath(
    '',
    ROUTES.WORKSPACES_ROUTE,
    ROUTES.WORKSPACE_BY_ID_ROUTE,
    ROUTES.WORKSPACE_BY_ID_SETTINGS_ROUTE,
    ROUTES.ORGANISATIONS_ROUTE,
    ROUTES.ORGANISATION_BY_ID_ROUTE,
    ROUTES.SETTINGS_OVERVIEW_PRODUCT_ROUTE
  );

  return matchPath(
    {
      path,
      end: false
    },
    pathname
  );
};

/**
 * This rebuilds the url from the main application, replacing the organisation id.
 *
 * @param {Organisation} org
 * @returns {string}
 */
export const changeOrganisation = (org: Organisation): string => {
  const basePath = joinRouteMatchPath(
    '',
    ROUTES.ROOT_ROUTE,
    ROUTES.WORKSPACES_ROUTE,
    ROUTES.WORKSPACE_BY_ID_ROUTE,
    ROUTES.ORGANISATIONS_ROUTE,
    ROUTES.ORGANISATION_BY_ID_ROUTE
  );
  return interpolatePathParams(basePath, {
    [ROUTE_PARAMS.WORKSPACE_ID]: org.workspace_id,
    [ROUTE_PARAMS.ORGANISATION_ID]: org.id
  });
};

/**
 * This rebuilds the url from the main application, replacing the product slug
 *
 * @param {Record<string, string | undefined>} params - Route parameters from useParams
 * @param {string} productSlug
 * @returns {string}
 */
export const changeProduct = (params: Record<string, string | undefined>, productSlug: string): string => {
  const basePath = getProductRoute();

  return interpolatePathParams(basePath, {
    [ROUTE_PARAMS.WORKSPACE_ID]: getParamFromRoute(params, ROUTE_PARAMS.WORKSPACE_ID) as string,
    [ROUTE_PARAMS.ORGANISATION_ID]: getParamFromRoute(params, ROUTE_PARAMS.ORGANISATION_ID) as string,
    [ROUTE_PARAMS.PRODUCT_SLUG]: productSlug
  });
};

export const getProductRoute = (): string =>
  joinRouteMatchPath(
    '',
    ROUTES.ROOT_ROUTE,
    ROUTES.WORKSPACES_ROUTE,
    ROUTES.WORKSPACE_BY_ID_ROUTE,
    ROUTES.ORGANISATIONS_ROUTE,
    ROUTES.ORGANISATION_BY_ID_ROUTE,
    ROUTES.PRODUCT_SLUG_ROUTE
  );

export const getProductConsolidationRoute = (): string =>
  joinRouteMatchPath(
    '',
    ROUTES.ROOT_ROUTE,
    ROUTES.WORKSPACES_ROUTE,
    ROUTES.WORKSPACE_BY_ID_ROUTE,
    ROUTES.CONSOLIDATIONS_ROUTE,
    ROUTES.ORGANISATION_BY_ID_ROUTE,
    ROUTES.PRODUCT_SLUG_ROUTE
  );

export const getInternalProductRoute = (): string =>
  joinRouteMatchPath(
    '',
    ROUTES.INTERNAL,
    ROUTES.WORKSPACES_ROUTE,
    ROUTES.WORKSPACE_BY_ID_ROUTE,
    ROUTES.ORGANISATIONS_ROUTE,
    ROUTES.ORGANISATION_BY_ID_ROUTE
  );

export const getInternalProductConsolidationRoute = (): string =>
  joinRouteMatchPath(
    '',
    ROUTES.INTERNAL,
    ROUTES.WORKSPACES_ROUTE,
    ROUTES.WORKSPACE_BY_ID_ROUTE,
    ROUTES.CONSOLIDATIONS_ROUTE,
    ROUTES.ORGANISATION_BY_ID_ROUTE
  );

export const getProductUrl = (workspaceId: string, organisationId: string, productSlug: string): string => {
  const basePath = getProductRoute();

  return interpolatePathParams(basePath, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.PRODUCT_SLUG]: productSlug.startsWith('/') ? productSlug.slice(1) : productSlug
  });
};

export const getWorkspaceUrl = (workspaceId: string): string => {
  const basePath = joinRouteMatchPath('', ROUTES.ROOT_ROUTE, ROUTES.WORKSPACES_ROUTE, ROUTES.WORKSPACE_BY_ID_ROUTE);

  return interpolatePathParams(basePath, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
};

export const getProductConsolidationUrl = (
  workspaceId: string,
  consolidationId: string,
  productSlug: string
): string => {
  const basePath = getProductConsolidationRoute();

  return interpolatePathParams(basePath, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.PRODUCT_SLUG]: productSlug.startsWith('/') ? productSlug.slice(1) : productSlug
  });
};

export const getCOAUrl = (workspaceId: string, organisationId: string): string => {
  const path = joinRouteMatchPath(
    '',
    ROUTES.WORKSPACES_ROUTE,
    ROUTES.WORKSPACE_BY_ID_ROUTE,
    ROUTES.ORGANISATION_BY_ID,
    ROUTES.PREDICT,
    ROUTES.CHART_OF_ACCOUNTS
  );

  return interpolatePathParams(path, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
  });
};

export const getCOAProfitAndLossRoute = (workspaceId: string, organisationId: string): string => {
  const path = joinRouteMatchPath(
    '',
    ROUTES.WORKSPACES_ROUTE,
    ROUTES.WORKSPACE_BY_ID_ROUTE,
    ROUTES.ORGANISATION_BY_ID,
    ROUTES.PREDICT,
    ROUTES.CHART_OF_ACCOUNTS,
    ROUTES.PROFIT_AND_LOSS
  );

  return interpolatePathParams(path, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
  });
};

export const getCOABalanceSheetRoute = (workspaceId: string, organisationId: string): string => {
  const path = joinRouteMatchPath(
    '',
    ROUTES.WORKSPACES_ROUTE,
    ROUTES.WORKSPACE_BY_ID_ROUTE,
    ROUTES.ORGANISATION_BY_ID,
    ROUTES.PREDICT,
    ROUTES.CHART_OF_ACCOUNTS,
    ROUTES.BALANCE_SHEET
  );

  return interpolatePathParams(path, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
  });
};
